import * as PIXI from 'pixi.js';
import { PixiCamera } from './PixiCamera.js';
import * as InventoryOverlay from './hud/InventoryOverlay.js';
import * as InventorySlots from './hud/InventorySlots.js';
import * as AbilitySlots from './hud/AbilitySlots.js';
import * as ShieldBar from './hud/ShieldBar.js';
import * as DriftBar from './hud/DriftBar.js';
import * as HealthBar from './hud/HealthBar.js';
import * as DeathNotification from './hud/DeathNotification.js';
import * as EliminationNotification from './hud/EliminationNotification.js';
import * as OffScreenHitIndicators from './hud/OffScreenHitIndicators.js';
import * as RewindMarker from './hud/RewindMarker.js';
import { pixiHudLogger } from './hud/logger.js';
import { HEALTH_MAX, SHIELD_MAX } from './hud/constants.js';
import * as RewindPrompt from './hud/RewindPrompt.js';
import * as InteractionPrompt from './hud/InteractionPrompt.js';
import * as RespawnCountdown from './hud/RespawnCountdown.js';
import * as DebugPanel from './hud/DebugPanel.js';
import * as AimReticle from './hud/AimReticle.js';
import * as MatchStats from './hud/MatchStats.js';
import * as ZonePanel from './hud/ZonePanel.js';
import * as KillFeed from './hud/KillFeed.js';
import * as Leaderboard from './hud/Leaderboard.js';
import * as Minimap from './hud/Minimap.js';
import * as MapOverlay from './hud/MapOverlay.js';


/**
 * PixiHUDRenderer.js - WebGL Heads-Up Display Rendering
 *
 * Handles rendering of all screen-space UI elements using PixiJS:
 * - Health bar
 * - Weapon/ammo HUD
 * - Ability HUD
 * - Elimination notifications
 * - Kill feed
 * - Debug info
 */

const PixiHUDRenderer = {
// Container reference
hudContainer: null,

// HUD element groups
elements: {
  healthBar: null,
  matchStats: null,
  abilitySlots: null,
  eliminationNotification: null,
  deathNotification: null,
  killFeed: null,
  debugPanel: null,
  leaderboard: null,
  aimReticle: null,
  zonePanel: null,
  minimap: null,
  mapOverlay: null
},

// Screen dimensions
screenWidth: 0,
screenHeight: 0,

// Cached references
localPlayer: null,
gameState: null,

// Gradient texture cache to prevent WebGL context loss
gradientTextureCache: {},

/**
 * Initialize the HUD renderer
 * @param {PIXI.Container} hudContainer - Container for HUD elements
 * @param {number} screenWidth - Screen width
 * @param {number} screenHeight - Screen height
 */
init(hudContainer, screenWidth, screenHeight, services = {}) {
  this.hudContainer = hudContainer;
  // Injected by PixiRenderer.init(); the HUD never looks up window.socketManager
  this.send = typeof services.send === 'function'
    ? services.send
    : (event) => pixiHudLogger.warn(`[PixiHUD] No send() service; dropped ${event}`);
  this.screenWidth = screenWidth;
  this.screenHeight = screenHeight;

  // Pre-create gradient textures to prevent WebGL context issues during gameplay
  this._precreateGradientTextures();

  // Create HUD element groups
  this._createHealthBar();
  this._createAbilitySlots();
  this._createInventoryOverlay(); // New inventory screen
  this._createEliminationNotification();
  this._createDeathNotification();
  this._createMinimap();    // before MatchStats: the icon row hangs off its bottom edge
  this._createMatchStats();
  this._createMapOverlay();
  this._createKillFeed();
  this._createDebugPanel();
  this._createLeaderboard();
  this._createAimReticle();
  this._createZonePanel();
  this._createInteractionPrompt();
  this._createRespawnCountdown();
  this._createRewindPrompt();
  this._createRewindMarker();
  this._createOffScreenHitIndicators();
},

/**
 * Update screen dimensions on resize
 */
resize(width, height) {
  this.screenWidth = width;
  this.screenHeight = height;
  this._repositionElements();
},

// ─────────────────────────────────────────────────────────────
// HEALTH BAR
// ─────────────────────────────────────────────────────────────

/**
 * Create health bar elements
 * @private
 */
_createHealthBar() {
  return HealthBar.create(this);
},

_createShieldBar() {
  return ShieldBar.create(this);
},

/**
 * Create the drift (hold-to-slide) meter
 * @private
 */
_createDriftBar() {
  return DriftBar.create(this);
},

/**
 * Position the drift meter under the health bar
 * @private
 */
_positionDriftBar() {
  return DriftBar.position(this);
},

/**
 * Update the drift meter
 * @param {Object} localPlayer - Local player data
 */
updateDriftBar(localPlayer) {
  return DriftBar.update(this, localPlayer);
},

/**
 * Position health bar
 * @private
 */
_positionHealthBar() {
  return HealthBar.position(this);
},

/**
 * Update health bar
 * @param {Object} localPlayer - Local player data
 */
updateHealthBar(localPlayer) {
  return HealthBar.update(this, localPlayer);
},

updateShieldBar(localPlayer) {
  return ShieldBar.update(this, localPlayer);
},

// ─────────────────────────────────────────────────────────────
// MATCH STATS (eliminations / deaths / players-remaining icon row)
// ─────────────────────────────────────────────────────────────

/**
 * Create match stats display
 * @private
 */
_createMatchStats() {
  return MatchStats.create(this);
},

/**
 * Position the icon row beneath the minimap in the top-right
 * @private
 */
_positionMatchStats() {
  return MatchStats.position(this, this.gameState);
},

/**
 * Update match stats display
 * @param {Object} gameState - Game state
 */
updateMatchStats(gameState) {
  return MatchStats.update(this, gameState);
},

// ─────────────────────────────────────────────────────────────
// ABILITY SLOTS
// ─────────────────────────────────────────────────────────────

/**
 * Create ability slots
 * @private
 */
_createAbilitySlots() {
  return AbilitySlots.create(this);
},

/**
 * Position ability slots
 * @private
 */
_positionAbilitySlots() {
  return AbilitySlots.position(this);
},

/**
 * Enable drag-and-drop for inventory slot reordering
 * @private
 * @param {Array} slots - Array of slot objects
 */
_enableInventoryDragAndDrop(slots) {
  let draggedSlot = null;
  let draggedIndex = null;
  let originalPosition = { x: 0, y: 0 };
  let dragOffset = { x: 0, y: 0 }; // Track offset from mouse to container origin

  slots.forEach((slot, index) => {
    const container = slot.container;
    
    // Make the container interactive
    container.eventMode = 'static';
    container.cursor = 'pointer';
    
    // Store the slot index on the container for easy access
    container.slotIndex = index;

    // Drag start
    container.on('pointerdown', (event) => {
      // Only allow dragging when inventory is open
      if (!this.gameState || !this.gameState.inventoryOpen) {
        return;
      }
      
      if (event.button !== 0) return; // Only left click
      
      draggedSlot = container;
      draggedIndex = index;
      
      // Store original position
      originalPosition.x = container.x;
      originalPosition.y = container.y;
      
      // Calculate offset from pointer to container position
      const localPos = event.getLocalPosition(container.parent);
      dragOffset.x = container.x - localPos.x;
      dragOffset.y = container.y - localPos.y;
      
      // Visual feedback: make it semi-transparent and slightly larger
      container.alpha = 0.7;
      container.scale.set(1.1);
      
      // Bring to front
      container.parent.setChildIndex(container, container.parent.children.length - 1);
      
      // Create drop indicator (highlight other slots)
      slots.forEach((s, i) => {
        if (i !== index) {
          s.border.clear();
          s.border.setStrokeStyle({ width: 3, color: 0x00ff00, alpha: 0.5 });
          s.border.rect(0, 0, 90, 90);
          s.border.stroke();
        }
      });
      
      event.stopPropagation();
    });

    // Drag move - needs to be on global document level to work smoothly
    container.on('globalpointermove', (event) => {
      if (draggedSlot === container) {
        const localPos = event.getLocalPosition(container.parent);
        container.x = localPos.x + dragOffset.x;
        container.y = localPos.y + dragOffset.y;
      }
    });

    // Drag end
    container.on('pointerup', (event) => {
      if (draggedSlot !== container) return;
      
      // Reset visual feedback
      container.alpha = 1.0;
      container.scale.set(1.0);
      
      // Find which slot we're over based on 60% overlap
      let targetIndex = null;
      const slotSize = 90;
      const overlapThreshold = 0.6; // 60% overlap required
      
      slots.forEach((s, i) => {
        if (i === draggedIndex) return;
        
        // Calculate overlap between dragged slot and target slot
        const draggedLeft = container.x - slotSize / 2;
        const draggedRight = container.x + slotSize / 2;
        const draggedTop = container.y - slotSize / 2;
        const draggedBottom = container.y + slotSize / 2;
        
        const targetLeft = s.container.x - slotSize / 2;
        const targetRight = s.container.x + slotSize / 2;
        const targetTop = s.container.y - slotSize / 2;
        const targetBottom = s.container.y + slotSize / 2;
        
        // Calculate intersection
        const overlapLeft = Math.max(draggedLeft, targetLeft);
        const overlapRight = Math.min(draggedRight, targetRight);
        const overlapTop = Math.max(draggedTop, targetTop);
        const overlapBottom = Math.min(draggedBottom, targetBottom);
        
        // Check if there's an overlap
        if (overlapRight > overlapLeft && overlapBottom > overlapTop) {
          const overlapWidth = overlapRight - overlapLeft;
          const overlapHeight = overlapBottom - overlapTop;
          const overlapArea = overlapWidth * overlapHeight;
          const slotArea = slotSize * slotSize;
          const overlapRatio = overlapArea / slotArea;
          
          // If at least 60% of the dragged slot overlaps with target, it's a swap
          if (overlapRatio >= overlapThreshold) {
            targetIndex = i;
          }
        }
      });
      
      if (targetIndex !== null && targetIndex !== draggedIndex) {
        // Emit event to swap slots
        this._onInventorySlotSwap(draggedIndex, targetIndex);
      } else if (targetIndex === null) {
        // No overlap with any slot - drop the item
        this._onInventoryItemDrop(draggedIndex);
      }
      
      // Reset position (will be updated by next render cycle)
      container.x = originalPosition.x;
      container.y = originalPosition.y;
      
      // Clear drop indicators
      slots.forEach((s) => {
        // Border will be redrawn in updateInventorySlots
        s.border.clear();
      });
      
      draggedSlot = null;
      draggedIndex = null;
    });

    // Drag cancel (pointer leaves)
    container.on('pointerupoutside', () => {
      if (draggedSlot === container) {
        container.alpha = 1.0;
        container.scale.set(1.0);
        container.x = originalPosition.x;
        container.y = originalPosition.y;
        
        slots.forEach((s) => {
          s.border.clear();
        });
        
        draggedSlot = null;
        draggedIndex = null;
      }
    });
  });
},

/**
 * Handle inventory slot swap event
 * @private
 * @param {number} fromIndex - Source slot index
 * @param {number} toIndex - Target slot index
 */
_onInventorySlotSwap(fromIndex, toIndex) {
  pixiHudLogger.info(`[PixiHUD] Swapping inventory slots: ${fromIndex} <-> ${toIndex}`);
  
  this.send('swapInventorySlots', { fromIndex, toIndex });
},

/**
 * Handle inventory item drop (drag outside inventory)
 * @private
 */
_onInventoryItemDrop(slotIndex) {
  pixiHudLogger.info(`[PixiHUD] Dropping item from slot: ${slotIndex}`);
  
  this.send('dropInventoryItem', { slotIndex });
},

/**
 * Drag an equipped ability out of its slot (with the inventory open) to drop it,
 * the same gesture that drops a weapon. Released mostly over another slot it is
 * left where it is; abilities are never swapped, each category has one slot.
 * @private
 * @param {Array} slots - ability slot objects (each has `container` and `config.category`)
 */
_enableAbilityDragToDrop(slots) {
  const slotSize = 90;
  const overlapThreshold = 0.6;
  let dragged = null; // { container, slot, home: {x, y}, offset: {x, y} }

  const equippedIn = (slot) => this.gameState?.abilityState?.equipped?.[slot.config.category];

  const endDrag = () => {
    if (!dragged) return;
    dragged.container.alpha = 1.0;
    dragged.container.scale.set(1.0);
    dragged.container.x = dragged.home.x;
    dragged.container.y = dragged.home.y;
    dragged = null;
  };

  // Does the dragged slot cover at least `overlapThreshold` of another slot?
  const coversAnotherSlot = (container) => {
    const others = [
      ...slots.filter((s) => s.container !== container),
      ...(this.elements.inventorySlots?.slots ?? [])
    ];
    return others.some((s) => {
      const overlapW = Math.min(container.x + slotSize, s.container.x + slotSize) - Math.max(container.x, s.container.x);
      const overlapH = Math.min(container.y + slotSize, s.container.y + slotSize) - Math.max(container.y, s.container.y);
      return overlapW > 0 && overlapH > 0 && (overlapW * overlapH) / (slotSize * slotSize) >= overlapThreshold;
    });
  };

  slots.forEach((slot) => {
    const container = slot.container;
    container.eventMode = 'static';
    container.cursor = 'pointer';

    container.on('pointerdown', (event) => {
      if (!this.gameState?.inventoryOpen) return;
      if (event.button !== 0) return;
      if (!equippedIn(slot)) return;

      const localPos = event.getLocalPosition(container.parent);
      dragged = {
        container,
        slot,
        home: { x: container.x, y: container.y },
        offset: { x: container.x - localPos.x, y: container.y - localPos.y }
      };
      container.alpha = 0.7;
      container.scale.set(1.1);
      container.parent.setChildIndex(container, container.parent.children.length - 1);
      event.stopPropagation();
    });

    container.on('globalpointermove', (event) => {
      if (dragged?.container !== container) return;
      const localPos = event.getLocalPosition(container.parent);
      container.x = localPos.x + dragged.offset.x;
      container.y = localPos.y + dragged.offset.y;
    });

    container.on('pointerup', () => {
      if (dragged?.container !== container) return;
      const { category } = slot.config;
      const drop = !coversAnotherSlot(container);
      endDrag();
      if (drop) this._onAbilityDrop(category);
    });

    container.on('pointerupoutside', endDrag);
  });
},

/**
 * Handle an ability dragged out of its slot.
 * @private
 * @param {string} category - 'utility' | 'movement'
 */
_onAbilityDrop(category) {
  pixiHudLogger.info(`[PixiHUD] Dropping ability from slot: ${category}`);

  this.send('dropAbility', { category });
},

/**
 * Create inventory overlay (TAB screen)
 * @private
 */
_createInventoryOverlay() {
  return InventoryOverlay.create(this);
},

/**
 * Update ability slots
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
updateAbilitySlots(gameState, callbacks) {
  return AbilitySlots.update(this, gameState, callbacks);
},

/**
 * Update inventory slots
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
updateInventorySlots(gameState, callbacks) {
  return InventorySlots.update(this, gameState, callbacks);
},

// ─────────────────────────────────────────────────────────────
// ELIMINATION NOTIFICATION
// ─────────────────────────────────────────────────────────────

/**
 * Create elimination notification
 * @private
 */
_createEliminationNotification() {
  return EliminationNotification.create(this);
},

/**
 * Update elimination notification
 * @param {Object} notification - Notification data
 * @param {Object} callbacks - Callback functions
 */
updateEliminationNotification(notification, callbacks) {
  return EliminationNotification.update(this, notification, callbacks);
},

_createDeathNotification() {
  return DeathNotification.create(this);
},

_createRespawnCountdown() {
  return RespawnCountdown.create(this);
},

updateRespawnCountdown(localPlayer) {
  return RespawnCountdown.update(this, localPlayer);
},

updateDeathNotification(notification, callbacks) {
  return DeathNotification.update(this, notification, callbacks);
},

// ─────────────────────────────────────────────────────────────
// KILL FEED
// ─────────────────────────────────────────────────────────────

/**
 * Create kill feed
 * @private
 */
_createKillFeed() {
  return KillFeed.create(this);
},

/**
 * Update kill feed
 * @param {Array} eliminationFeed - Feed entries
 * @param {string} localId - Local player ID
 * @param {Object} callbacks - Callback functions
 */
updateKillFeed(eliminationFeed, localId, callbacks) {
  return KillFeed.update(this, eliminationFeed, localId, callbacks);
},

_createAimReticle() {
  return AimReticle.create(this);
},

updateAimReticle(gameState) {
  return AimReticle.update(this, gameState);
},

// ─────────────────────────────────────────────────────────────
// DEBUG PANEL
// ─────────────────────────────────────────────────────────────

/**
 * Create debug panel
 * @private
 */
_createDebugPanel() {
  return DebugPanel.create(this);
},

/**
 * Update debug panel
 * @param {Object} debugInfo - Debug information object
 */
updateDebugPanel(debugInfo) {
  return DebugPanel.update(this, debugInfo);
},

// ─────────────────────────────────────────────────────────────
// LEADERBOARD
// ─────────────────────────────────────────────────────────────

/**
 * Create leaderboard display
 * @private
 */
_createLeaderboard() {
  return Leaderboard.create(this);
},

/**
 * Position leaderboard in top-left corner
 * @private
 */
_positionLeaderboard() {
  return Leaderboard.position(this);
},

/**
 * Update leaderboard
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
updateLeaderboard(gameState, callbacks) {
  return Leaderboard.update(this, gameState, callbacks);
},

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

/**
 * Position shield bar
 * @private
 */
_positionShieldBar() {
  return ShieldBar.position(this);
},

/**
 * Create a text object
 * @private
 */
_createText(content, fontSize, color, bold = false) {
  return new PIXI.Text({
    text: content,
    style: {
      fontFamily: 'Arial',
      fontSize: fontSize,
      fontWeight: bold ? 'bold' : 'normal',
      fill: color
    }
  });
},

/**
 * Reposition all elements after resize
 * @private
 */
_repositionElements() {
  this._positionHealthBar();
  this._positionAbilitySlots();
  this._positionShieldBar();
  this._positionDriftBar();
  this._positionLeaderboard();
  this._positionZonePanel();
  this._positionInteractionPrompt();
  this._positionRewindPrompt();
  this._positionMinimap();
  this._positionMatchStats(); // after the minimap it anchors to
  this._positionMapOverlay();
},

/**
 * Main update function - call every frame
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
update(gameState, callbacks) {
  this.gameState = gameState; // Store reference for event handlers
  const localPlayer = gameState.players.get(gameState.localId);

  if (localPlayer) {
    this.updateHealthBar(localPlayer);
    this.updateDriftBar(localPlayer);
    this.updateRespawnCountdown(localPlayer);
  }

  this.updateZonePanel(gameState);
  this.updateInteractionPrompt(gameState);
  this.updateMatchStats(gameState);
  this.updateAbilitySlots(gameState, callbacks);
  this.updateInventorySlots(gameState, callbacks);
  this.updateLeaderboard(gameState, callbacks);
  this.updateEliminationNotification(gameState.eliminationNotification, callbacks);
  this.updateAimReticle(gameState);
  this.updateDeathNotification(gameState.deathNotification, callbacks);
  this.updateKillFeed(gameState.eliminationFeed, gameState.localId, callbacks);
  this.updateRewindPrompt(gameState);
  this.updateRewindMarker(gameState);
  this.updateOffScreenHitIndicators(gameState);
  this.updateMinimap(gameState, callbacks);
  this.updateMapOverlay(gameState, callbacks);

  // Toggle inventory overlay visibility based on gameState (TAB)
  if (this.elements.inventoryOverlay) {
    this.elements.inventoryOverlay.container.visible = gameState.inventoryOpen || false;
  }
  
  this.updateDebugPanel({
    debugMode: callbacks.DEBUG_MODE,
    mouseWorldX: callbacks.mouseWorldX,
    mouseWorldY: callbacks.mouseWorldY,
    fps: callbacks.fps,
    ping: callbacks.ping,
    netStats: callbacks.netStats,
    rendererType: callbacks.rendererType
  });
},

_createZonePanel() {
  return ZonePanel.create(this);
},

_positionZonePanel() {
  return ZonePanel.position(this);
},

updateZonePanel(gameState) {
  return ZonePanel.update(this, gameState);
},

// ─────────────────────────────────────────────────────────────
// MINIMAP + LARGE MAP OVERLAY (M)
// ─────────────────────────────────────────────────────────────

_createMinimap() {
  return Minimap.create(this);
},

_positionMinimap() {
  return Minimap.position(this);
},

updateMinimap(gameState, callbacks) {
  return Minimap.update(this, gameState, callbacks);
},

_createMapOverlay() {
  return MapOverlay.create(this);
},

_positionMapOverlay() {
  return MapOverlay.position(this);
},

updateMapOverlay(gameState, callbacks) {
  return MapOverlay.update(this, gameState, callbacks);
},

_formatZoneTimer(timeMs = 0) {
  const totalSeconds = Math.max(0, Math.floor(timeMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
},

_isTakingZoneDamage(gameState) {
  const lastTick = gameState.lastZoneDamage;
  if (!lastTick) return false;
  const now = (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : Date.now();
  return (now - (lastTick.receivedAt || now)) < 1500;
},

_createInteractionPrompt() {
  return InteractionPrompt.create(this);
},

_positionInteractionPrompt() {
  return InteractionPrompt.position(this);
},

updateInteractionPrompt(gameState) {
  return InteractionPrompt.update(this, gameState);
},

/**
 * Create rewind ability prompt
 * @private
 */
_createRewindPrompt() {
  return RewindPrompt.create(this);
},

/**
 * Position rewind prompt
 * @private
 */
_positionRewindPrompt() {
  return RewindPrompt.position(this);
},

/**
 * Update rewind prompt visibility
 * @param {Object} gameState - Game state
 */
updateRewindPrompt(gameState) {
  return RewindPrompt.update(this, gameState);
},

// ─────────────────────────────────────────────────────────────
// REWIND MARKER
// ─────────────────────────────────────────────────────────────

/**
 * Create rewind start position marker
 * @private
 */
_createRewindMarker() {
  return RewindMarker.create(this);
},

/**
 * Update rewind marker position and visibility
 * @param {Object} gameState - Game state
 */
updateRewindMarker(gameState) {
  return RewindMarker.update(this, gameState);
},

// ─────────────────────────────────────────────────────────────
// OFF-SCREEN HIT INDICATORS
// ─────────────────────────────────────────────────────────────

/**
 * Create off-screen hit indicator container
 * @private
 */
_createOffScreenHitIndicators() {
  return OffScreenHitIndicators.create(this);
},

/**
 * Update off-screen hit indicators
 * @param {Object} gameState - Game state
 */
updateOffScreenHitIndicators(gameState) {
  return OffScreenHitIndicators.update(this, gameState);
},

/**
 * Create a single hit indicator graphic
 * @private
 */
_createHitIndicatorGraphic(hit, elem) {
  const container = new PIXI.Container();

  // Calculate position using PixiCamera
  const screenPos = PixiCamera.worldToScreen(hit.targetX, hit.targetY);
  const screenX = screenPos.x;
  const screenY = screenPos.y;

  const centerX = this.screenWidth / 2;
  const centerY = this.screenHeight / 2;
  const dx = screenX - centerX;
  const dy = screenY - centerY;
  const angle = Math.atan2(dy, dx);

  const margin = 60;
  const edgeX = centerX + Math.cos(angle) * (this.screenWidth / 2 - margin);
  const edgeY = centerY + Math.sin(angle) * (this.screenHeight / 2 - margin);

  const clampedX = Math.max(margin, Math.min(this.screenWidth - margin, edgeX));
  const clampedY = Math.max(margin, Math.min(this.screenHeight - margin, edgeY));

  container.x = clampedX;
  container.y = clampedY;
  container.rotation = angle;

  // Hit marker graphic (chevron/arrow pointing to hit direction)
  const arrow = new PIXI.Graphics();
  arrow.setFillStyle({ color: 0xff4444, alpha: 0.8 });
  arrow.setStrokeStyle({ width: 2, color: 0xffffff });
  
  // Double chevron for emphasis
  arrow.moveTo(15, 0);
  arrow.lineTo(5, -10);
  arrow.lineTo(5, 10);
  arrow.closePath();
  arrow.fill();
  arrow.stroke();
  
  arrow.moveTo(25, 0);
  arrow.lineTo(15, -10);
  arrow.lineTo(15, 10);
  arrow.closePath();
  arrow.fill();
  arrow.stroke();
  
  container.addChild(arrow);

  // Damage text
  const damageText = new PIXI.Text({
    text: `-${Math.round(hit.damage)}`,
    style: {
      fontFamily: 'Arial',
      fontSize: 18,
      fontWeight: 'bold',
      fill: hit.shieldDamage > 0 ? 0x00d4ff : 0xff4444,
      stroke: { color: 0x000000, width: 3 }
    }
  });
  // Position text on opposite side when pointing left to avoid overlap
  const pointingLeft = Math.abs(angle) > Math.PI / 2;
  damageText.anchor.set(pointingLeft ? 1 : 0, 0.5);
  damageText.x = pointingLeft ? -10 : 30;
  damageText.y = 0;
  // Counter-rotate to stay readable regardless of arrow direction
  damageText.rotation = -angle;
  container.addChild(damageText);

  elem.container.addChild(container);

  elem.indicators.push({
    graphics: container,
    damageText: damageText, // Store reference for counter-rotation
    targetX: hit.targetX,
    targetY: hit.targetY,
    timestamp: hit.timestamp,
    duration: hit.duration
  });
},

/**
 * Reset renderer state for context recovery
 * Called when WebGL context is restored
 */
reset() {
  // Clear gradient texture cache - textures are invalid after context loss
  this._clearGradientCache();

  // Remove event listeners from inventory slots to prevent leaks
  this._removeInventoryEventListeners();

  // Pre-create gradient textures before recreating elements
  this._precreateGradientTextures();

  // Recreate HUD elements
  if (this.hudContainer) {
    for (const child of this.hudContainer.removeChildren()) {
      child.destroy({ children: true });
    }
  }

  // Reinitialize all elements
  this._createHealthBar();
  this._createAbilitySlots();
  this._createInventoryOverlay();
  this._createEliminationNotification();
  this._createDeathNotification();
  this._createMinimap();    // before MatchStats: the icon row hangs off its bottom edge
  this._createMatchStats();
  this._createMapOverlay();
  this._createKillFeed();
  this._createDebugPanel();
  this._createLeaderboard();
  this._createAimReticle();
  this._createZonePanel();
  this._createInteractionPrompt();
  this._createRespawnCountdown();
  this._createRewindPrompt();
  this._createRewindMarker();
  this._createOffScreenHitIndicators();

  pixiHudLogger.info('[PixiHUDRenderer] Reset complete - HUD elements recreated');
},

/**
 * Clear the gradient texture cache
 * @private
 */
_clearGradientCache() {
  for (const key in this.gradientTextureCache) {
    if (this.gradientTextureCache[key]) {
      this.gradientTextureCache[key].destroy(true);
    }
  }
  this.gradientTextureCache = {};
},

/**
 * Pre-create gradient textures for inventory slots
 * This prevents creating canvas 2D contexts during gameplay which can cause WebGL context loss
 * @private
 */
_precreateGradientTextures() {
  const slotSize = 90; // Standard inventory slot size
  const stackColors = {
    1: 0x3b82f6,  // Blue
    2: 0x22c55e,  // Green
    3: 0xa855f7,  // Purple
    4: 0xeab308   // Gold
  };

  for (const stackCount in stackColors) {
    const gradientColor = stackColors[stackCount];
    const cacheKey = `gradient_${gradientColor}_${slotSize}`;

    // Skip if already cached
    if (this.gradientTextureCache[cacheKey]) continue;

    // Create gradient texture
    const canvas = document.createElement('canvas');
    canvas.width = slotSize;
    canvas.height = slotSize;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, slotSize);

    // Convert hex to RGB for gradient
    const r = (gradientColor >> 16) & 0xFF;
    const g = (gradientColor >> 8) & 0xFF;
    const b = gradientColor & 0xFF;

    gradient.addColorStop(0, `rgb(${r}, ${g}, ${b})`);
    gradient.addColorStop(1, 'rgb(255, 255, 255)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, slotSize, slotSize);

    this.gradientTextureCache[cacheKey] = PIXI.Texture.from(canvas);
  }

  pixiHudLogger.debug('[PixiHUDRenderer] Pre-created', Object.keys(this.gradientTextureCache).length, 'gradient textures');
},

/**
 * Remove event listeners from inventory slots
 * @private
 */
_removeInventoryEventListeners() {
  const invSlots = this.elements.inventorySlots;
  if (invSlots && invSlots.slots) {
    invSlots.slots.forEach((slot) => {
      if (slot.container) {
        slot.container.removeAllListeners();
      }
    });
  }
},

/**
 * Clean up resources
 */
destroy() {
  // Clear gradient texture cache
  this._clearGradientCache();

  // Remove event listeners from inventory slots
  this._removeInventoryEventListeners();

  // Destroy the container and all children
  if (this.hudContainer) {
    this.hudContainer.destroy({ children: true });
  }

  // Clear element references
  this.elements = {
    healthBar: null,
    matchStats: null,
    abilitySlots: null,
    eliminationNotification: null,
    deathNotification: null,
    killFeed: null,
    debugPanel: null,
    leaderboard: null,
    aimReticle: null,
    zonePanel: null
  };
}
};

// Export for use in other modules

export { pixiHudLogger, HEALTH_MAX, SHIELD_MAX, PixiHUDRenderer };
